import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useSubscriptionPaywall } from 'app/main/subscriptionManagement/SubscriptionPaywallContext';
import { memo, useEffect, useState } from 'react';
import { hasPermission } from 'app/main/loadz/utils';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import downloadIcon from './icons/download.svg';
import plusIcon from './icons/plus.svg';
import searchIcon from './icons/search.svg';
import filterIcon from './icons/filter.svg';

const getOriginName = (job) => job?.quarry_pit?.l_name || job?.j_origin?.l_name || '';

function csvCell(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function JobsHeader({ setSearch, changeJobFilter, data, setEditJobOpen, setJob_id, setTicketText, ticketText }) {
  const { t } = useTranslation('navigation');
  const [text, setText] = useState('');
  const history = useHistory();
  const { blockNewJob } = useSubscriptionPaywall();
  const jobs = useSelector((state) => state.loadz_jobs?.JobReducer?.jobs) || [];

  const [og, setog] = useState(false);
  const [sub, setsub] = useState(false);
  const [archive, setarchive] = useState(false);
  const [drafts, setDrafts] = useState(false);
  const [commissions, setCommissions] = useState(false);
  const [visibility, setVisibility] = useState(false);
  const [all, setall] = useState(true);
  const [filterMenu, setFilterMenu] = useState(null);

  useEffect(() => {
    if (text.length > 0) {
      setSearch(text);
      setTicketText('');
    } else if (ticketText.length > 0) {
      setSearch(ticketText);
      setText('');
    } else if (text.length == 0 && ticketText.length == 0) {
      setSearch('');
    }
  }, [text, ticketText]);

  const applyFilter = (value) => {
    changeJobFilter(value);
    setall(value === '');
    setog(value === 'OG');
    setsub(value === 'sub');
    setarchive(value === 'archived');
    setCommissions(value === 'commissions');
    setDrafts(value === 'drafts');
    setVisibility(value === 'visibility');
    setFilterMenu(null);
  };

  const openNewJob = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (blockNewJob()) {
      return;
    }
    if (hasPermission('jobs.create') == true && data) {
      setEditJobOpen(true);
      setJob_id('');
      return;
    }
    history.push('/jobs/new');
  };

  const exportJobs = () => {
    const headers = [
      t('JOB_NO'),
      t('CUSTOMER'),
      t('ORIGIN'),
      t('DESTINATION'),
      t('MATERIAL'),
      t('QUANTITY'),
      t('COMPLETED_TICKETS'),
      t('LAST_ACTIVITY'),
    ];
    const rows = jobs.map((job) => {
      const quantity = job?.ticketcompleted ? Number(job.ticketcompleted).toFixed(1) : '-';
      const unit = job?.job_billing?.unit_name || '';
      const lastActivity =
        !job?.recent_tk || job.recent_tk == 0
          ? t('NO_RECENT_ACTIVITY')
          : moment(job.recent_tk).format('MMM D, YYYY h:mm A');
      return [
        `${job.company_id}-${job.job_no}`,
        job.customer_name || '',
        getOriginName(job),
        job?.j_destination?.l_name || '',
        job?.job_billing?.name || '',
        `${quantity}${unit ? ` ${unit}` : ''}`,
        job?.ticketCounts ?? '',
        lastActivity,
      ];
    });
    const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'jobs.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const primaryFilters = [
    { id: 'all', label: t('ALL_JOBS'), value: '', active: all },
    { id: 'owner', label: t('OWNER'), value: 'OG', active: og },
    { id: 'sub', label: t('SUBCONTRACTOR'), value: 'sub', active: sub },
    { id: 'archive', label: t('ARCHIVE'), value: 'archived', active: archive },
  ];

  const extraFilters = [
    { id: 'commissions', label: t('COMMISSIONS'), value: 'commissions', active: commissions },
    { id: 'drafts', label: t('DRAFTS'), value: 'drafts', active: drafts },
    { id: 'disabled', label: t('DISABLED'), value: 'visibility', active: visibility },
  ];

  const extraFilterActive = commissions || drafts || visibility;

  return (
    <div className="jobs-redesign-header">
      <div className="jobs-redesign-title-row">
        <div className="jobs-redesign-title">
          <Typography component="h1">{t('JOBS')}</Typography>
          <Typography component="p">
            {t('JOBS_LIST_SUBTITLE', {
              defaultValue: 'Manage hauling jobs, routes, quantities and completed tickets.',
            })}
          </Typography>
        </div>
        <div className="jobs-redesign-title-actions">
          <Button
            className="jobs-redesign-export"
            variant="outlined"
            onClick={exportJobs}
            disabled={jobs.length === 0}
            startIcon={<img src={downloadIcon} alt="" />}
          >
            {t('EXPORT')}
          </Button>
          <Button
            className="jobs-redesign-add"
            disableElevation
            onClick={openNewJob}
            startIcon={<img src={plusIcon} alt="" />}
          >
            <span>{t('ADD_NEW_JOB')}</span>
          </Button>
        </div>
      </div>

      <div className="jobs-redesign-toolbar">
        <div className="jobs-redesign-searches">
          <div className="jobs-redesign-search jobs-redesign-search-jobs">
            <img src={searchIcon} alt="" />
            <Input
              placeholder={t('SEARCH_JOBS_CUSTOMERS_OR_LOCATIONS', {
                defaultValue: 'Search jobs, customers or locations...',
              })}
              disableUnderline
              fullWidth
              value={text}
              type="text"
              inputProps={{
                'aria-label': t('SEARCH'),
              }}
              onChange={(ev) => {
                ev.preventDefault();
                setText(ev.target.value);
                if (ticketText.length > 0) {
                  setTicketText('');
                }
              }}
            />
          </div>
          <div className="jobs-redesign-search jobs-redesign-search-ticket">
            <img src={searchIcon} alt="" />
            <Input
              placeholder={t('SEARCH_TICKET_NUMBER', {
                defaultValue: 'Search ticket number',
              })}
              disableUnderline
              fullWidth
              value={ticketText}
              type="text"
              inputProps={{
                'aria-label': t('SEARCH_TICKET'),
              }}
              onChange={(ev) => {
                ev.preventDefault();
                setTicketText(ev.target.value);
                if (text.length > 0) {
                  setText('');
                }
              }}
            />
          </div>
        </div>

        <div className="jobs-redesign-filters">
          {primaryFilters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={filter.active ? 'jobs-redesign-pill is-active' : 'jobs-redesign-pill'}
              onClick={() => applyFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
          <Button
            className={
              extraFilterActive ? 'jobs-redesign-filter-btn is-active' : 'jobs-redesign-filter-btn'
            }
            variant="outlined"
            onClick={(event) => setFilterMenu(event.currentTarget)}
            startIcon={<img src={filterIcon} alt="" />}
          >
            {t('FILTERS')}
          </Button>
          <Menu
            anchorEl={filterMenu}
            open={Boolean(filterMenu)}
            onClose={() => setFilterMenu(null)}
          >
            {extraFilters.map((filter) => (
              <MenuItem
                key={filter.id}
                selected={filter.active}
                onClick={() => applyFilter(filter.value)}
              >
                {filter.label}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>
    </div>
  );
}

export default memo(JobsHeader);
